import { CONECTA_PLATFORM } from '@/config/conectaPlatform'
import { INSTANCE_PROFILE, type InstanceProfile } from '@/config/instanceProfile'

/** Paths públicos Rocket (espelham `sidebarLogos` — sem import cruzado). */
const LOGO_ROCKET_DARK = '/logo-rocket-dark.png'
const LOGO_ROCKET_LIGHT = '/logo-rocket-light.svg'

// Configuração completa de tema white-label
export interface ThemeConfig {
  mode: 'dark' | 'light'

  // Cores Primárias (botões, links, destaques)
  brandPrimary: string
  brandPrimaryHover: string
  brandPrimaryMuted: string

  // Cores Secundárias (sucesso, confirmações)
  brandSecondary: string
  brandSecondaryHover: string

  // Tipografia — Hierarquia de cores
  fontColorH1: string
  fontColorH2: string
  fontColorH3: string
  fontColorH4: string
  fontColorH5: string
  fontColorH6: string
  fontColorBody: string
  fontColorMuted: string
  fontColorLabel: string

  // Tipografia — Tamanhos
  fontSizeHero: string
  fontSizeH1: string
  fontSizeH2: string
  fontSizeH3: string
  fontSizeH4: string
  fontSizeH5: string
  fontSizeH6: string
  fontSizeBody: string
  fontSizeSm: string
  fontSizeXs: string
  fontSizeLabel: string

  // Identidade da empresa
  companyName: string
  logoUrl: string | null
  logoUrlLight: string | null
  logoUrlDark: string | null

  // Superfícies — background das áreas principais (sidebar, topbar, conteúdo)
  bgDark: string      // fundo base mais escuro (corpo da página)
  bgMain: string      // fundo do conteúdo principal + header
  bgHeader: string    // fundo da topbar (supertopo)
  textHeader: string  // texto/ícones da topbar (supertopo)
  bgSidebar: string   // fundo do menu lateral
  textSidebar: string // cor do texto no menu lateral
  bgCard: string      // fundo de cards e painéis

  /** Degradê lavanda no menu + supertopo (tema claro): cor e intensidade 0–100 */
  degradeColor: string
  degradePercent: number

  /** Item ativo do menu lateral */
  navActiveColor: string
  navActiveMuted: string

  // Texto de superfície (contraste sobre os backgrounds acima)
  textPrimary: string
  textSecondary: string
  textMuted: string
}

/** Identidade Rocket Search — usada no default do tema e no heal de SystemSettings. */
export const ROCKET_BRAND_IDENTITY = {
  companyName: 'Rocket Search',
  logoUrl: LOGO_ROCKET_LIGHT,
  logoUrlLight: LOGO_ROCKET_LIGHT,
  logoUrlDark: LOGO_ROCKET_DARK,
  brandPrimary: '#e91e63',
  brandPrimaryHover: '#ff4f8f',
  brandPrimaryMuted: 'rgba(233, 30, 99, 0.16)',
  navActiveColor: '#e91e63',
  navActiveMuted: 'rgba(233, 30, 99, 0.18)',
} as const

export const ROCKET_DARK_SURFACES = {
  bgDark: '#0d0221',
  bgMain: '#12062a',
  bgSidebar: '#0d0221',
  bgHeader: '#14062d',
  bgCard: '#180833',
  textHeader: '#ffffff',
  textSidebar: '#f7edf7',
  textPrimary: '#ffffff',
  textSecondary: '#e9dff0',
  textMuted: '#bba9c8',
  fontColorH1: '#ffffff',
  fontColorH2: '#f8edf8',
  fontColorH3: '#eadcf2',
  fontColorH4: '#dccbe8',
  fontColorH5: '#cdb9dd',
  fontColorH6: '#bba9c8',
  fontColorBody: '#e9dff0',
  fontColorMuted: '#bba9c8',
  fontColorLabel: '#d7b7c9',
} as const

const BASE_DEFAULT_THEME: ThemeConfig = {
  mode: 'light',
  brandPrimary: '#5e6ad2',
  brandPrimaryHover: '#7c83db',
  brandPrimaryMuted: 'rgba(94, 106, 210, 0.15)',
  brandSecondary: '#22c55e',
  brandSecondaryHover: '#16a34a',
  fontColorH1: '#f5f5f6',
  fontColorH2: '#e8e8ec',
  fontColorH3: '#d0d0d8',
  fontColorH4: '#c8c8d0',
  fontColorH5: '#b8b8c0',
  fontColorH6: '#a8a8b0',
  fontColorBody: '#b4b4be',
  fontColorMuted: '#8a8a95',
  fontColorLabel: '#62626a',
  fontSizeHero: '2rem',
  fontSizeH1: '1.5rem',
  fontSizeH2: '1.25rem',
  fontSizeH3: '1.125rem',
  fontSizeH4: '1rem',
  fontSizeH5: '0.875rem',
  fontSizeH6: '0.75rem',
  fontSizeBody: '0.9375rem',
  fontSizeSm: '0.8125rem',
  fontSizeXs: '0.75rem',
  fontSizeLabel: '0.6875rem',
  companyName: CONECTA_PLATFORM.name,
  logoUrl: null,
  logoUrlLight: null,
  logoUrlDark: null,
  // Superfícies — dark defaults
  bgDark: '#08090a',
  bgMain: '#0c0d0e',
  bgHeader: '#0c0d0e',
  textHeader: '#f5f5f6',
  bgSidebar: '#0a0b0c',
  textSidebar: '#b4b4be',
  bgCard: '#141516',
  textPrimary: '#f5f5f6',
  textSecondary: '#b4b4be',
  textMuted: '#8a8a95',
  degradeColor: '#ebe5f9',
  degradePercent: 0,
  navActiveColor: '#5e6ad2',
  navActiveMuted: 'rgba(94, 106, 210, 0.16)',
}

export function getDefaultTheme(profile: InstanceProfile = INSTANCE_PROFILE): ThemeConfig {
  const identity =
    profile === 'rocket'
      ? {
          companyName: ROCKET_BRAND_IDENTITY.companyName,
          logoUrl: ROCKET_BRAND_IDENTITY.logoUrl,
          logoUrlLight: ROCKET_BRAND_IDENTITY.logoUrlLight,
          logoUrlDark: ROCKET_BRAND_IDENTITY.logoUrlDark,
        }
      : {}
  return {
    ...BASE_DEFAULT_THEME,
    ...identity,
    ...getModeSurfaceDefaults('light', profile),
  }
}

export function withRocketThemeDefaults(
  theme: ThemeConfig,
  profile: InstanceProfile = INSTANCE_PROFILE,
): ThemeConfig {
  if (profile !== 'rocket') return theme
  if (theme.mode !== 'dark') {
    return {
      ...theme,
      brandPrimary: ROCKET_BRAND_IDENTITY.brandPrimary,
      brandPrimaryHover: ROCKET_BRAND_IDENTITY.brandPrimaryHover,
      brandPrimaryMuted: ROCKET_BRAND_IDENTITY.brandPrimaryMuted,
      navActiveColor: ROCKET_BRAND_IDENTITY.navActiveColor,
      navActiveMuted: ROCKET_BRAND_IDENTITY.navActiveMuted,
    }
  }
  return {
    ...theme,
    ...ROCKET_DARK_SURFACES,
    brandPrimary: ROCKET_BRAND_IDENTITY.brandPrimary,
    brandPrimaryHover: ROCKET_BRAND_IDENTITY.brandPrimaryHover,
    brandPrimaryMuted: ROCKET_BRAND_IDENTITY.brandPrimaryMuted,
    navActiveColor: ROCKET_BRAND_IDENTITY.navActiveColor,
    navActiveMuted: ROCKET_BRAND_IDENTITY.navActiveMuted,
    degradeColor: '#fce4ec',
    degradePercent: 0,
  }
}

/** Valores padrão do menu white Agrada (lavanda + item ativo roxo) — só presets da aba Aparência */
export const lightMenuPresetDefaults: Pick<
  ThemeConfig,
  | 'degradeColor'
  | 'degradePercent'
  | 'navActiveColor'
  | 'navActiveMuted'
  | 'bgSidebar'
  | 'bgHeader'
  | 'brandPrimary'
  | 'brandPrimaryHover'
  | 'brandPrimaryMuted'
> = {
  degradeColor: '#ebe5f9',
  degradePercent: 40,
  navActiveColor: '#987de8',
  navActiveMuted: 'rgba(152, 125, 232, 0.16)',
  bgSidebar: '#ffffff',
  bgHeader: '#ffffff',
  brandPrimary: '#987de8',
  brandPrimaryHover: '#7c66d4',
  brandPrimaryMuted: 'rgba(152, 125, 232, 0.14)',
}

/** Menu white Rocket — rosa da marca, sem roxo Agrada */
export const rocketLightMenuDefaults: Pick<
  ThemeConfig,
  | 'degradeColor'
  | 'degradePercent'
  | 'navActiveColor'
  | 'navActiveMuted'
  | 'bgSidebar'
  | 'bgHeader'
  | 'brandPrimary'
  | 'brandPrimaryHover'
  | 'brandPrimaryMuted'
> = {
  degradeColor: '#fce4ec',
  degradePercent: 35,
  navActiveColor: ROCKET_BRAND_IDENTITY.navActiveColor,
  navActiveMuted: ROCKET_BRAND_IDENTITY.navActiveMuted,
  bgSidebar: '#ffffff',
  bgHeader: '#ffffff',
  brandPrimary: ROCKET_BRAND_IDENTITY.brandPrimary,
  brandPrimaryHover: ROCKET_BRAND_IDENTITY.brandPrimaryHover,
  brandPrimaryMuted: ROCKET_BRAND_IDENTITY.brandPrimaryMuted,
}

/**
 * Superfícies ao alternar dark/light no header.
 * Não sobrescreve brandPrimary/nav da identidade (Rocket rosa permanece nos dois modos).
 */
export function getModeSurfaceDefaults(
  mode: 'dark' | 'light',
  profile: InstanceProfile = INSTANCE_PROFILE,
): Partial<ThemeConfig> {
  if (mode === 'light') {
    const menu =
      profile === 'rocket'
        ? {
            degradeColor: rocketLightMenuDefaults.degradeColor,
            degradePercent: rocketLightMenuDefaults.degradePercent,
            bgSidebar: rocketLightMenuDefaults.bgSidebar,
            bgHeader: rocketLightMenuDefaults.bgHeader,
            navActiveColor: rocketLightMenuDefaults.navActiveColor,
            navActiveMuted: rocketLightMenuDefaults.navActiveMuted,
            brandPrimary: rocketLightMenuDefaults.brandPrimary,
            brandPrimaryHover: rocketLightMenuDefaults.brandPrimaryHover,
            brandPrimaryMuted: rocketLightMenuDefaults.brandPrimaryMuted,
          }
        : {
            degradeColor: lightMenuPresetDefaults.degradeColor,
            degradePercent: lightMenuPresetDefaults.degradePercent,
            bgSidebar: lightMenuPresetDefaults.bgSidebar,
            bgHeader: lightMenuPresetDefaults.bgHeader,
            navActiveColor: lightMenuPresetDefaults.navActiveColor,
            navActiveMuted: lightMenuPresetDefaults.navActiveMuted,
            brandPrimary: lightMenuPresetDefaults.brandPrimary,
            brandPrimaryHover: lightMenuPresetDefaults.brandPrimaryHover,
            brandPrimaryMuted: lightMenuPresetDefaults.brandPrimaryMuted,
          }
    return {
      bgDark: '#f0f0f3',
      bgMain: '#f8f8fa',
      bgCard: '#ffffff',
      textHeader: '#09090b',
      textSidebar: '#18181b',
      textPrimary: '#09090b',
      textSecondary: '#18181b',
      textMuted: '#3f3f46',
      fontColorH1: '#09090b',
      fontColorH2: '#18181b',
      fontColorH3: '#27272a',
      fontColorH4: '#3f3f46',
      fontColorH5: '#52525b',
      fontColorH6: '#71717a',
      fontColorBody: '#3f3f46',
      fontColorMuted: '#71717a',
      fontColorLabel: '#a1a1aa',
      ...menu,
    }
  }

  const darkNav =
    profile === 'rocket'
      ? {
          navActiveColor: ROCKET_BRAND_IDENTITY.navActiveColor,
          navActiveMuted: ROCKET_BRAND_IDENTITY.navActiveMuted,
          brandPrimary: ROCKET_BRAND_IDENTITY.brandPrimary,
          brandPrimaryHover: ROCKET_BRAND_IDENTITY.brandPrimaryHover,
          brandPrimaryMuted: ROCKET_BRAND_IDENTITY.brandPrimaryMuted,
        }
      : {
          navActiveColor: '#5e6ad2',
          navActiveMuted: 'rgba(94, 106, 210, 0.16)',
        }

  return {
    ...(profile === 'rocket'
      ? ROCKET_DARK_SURFACES
      : {
          bgDark: '#08090a',
          bgMain: '#0c0d0e',
          bgSidebar: '#0a0b0c',
          bgCard: '#141516',
          bgHeader: '#0c0d0e',
          textHeader: '#f5f5f6',
          textSidebar: '#b4b4be',
          textPrimary: '#f5f5f6',
          textSecondary: '#b4b4be',
          textMuted: '#8a8a95',
          fontColorH1: '#f5f5f6',
          fontColorH2: '#e8e8ec',
          fontColorH3: '#d0d0d8',
          fontColorH4: '#c8c8d0',
          fontColorH5: '#b8b8c0',
          fontColorH6: '#a8a8b0',
          fontColorBody: '#b4b4be',
          fontColorMuted: '#8a8a95',
          fontColorLabel: '#62626a',
        }),
    degradeColor: profile === 'rocket' ? '#fce4ec' : '#ebe5f9',
    degradePercent: 0,
    ...darkNav,
  }
}

/** Default da instância atual (Rocket neste repo). */
export const defaultTheme: ThemeConfig = getDefaultTheme()
